/**
 * Small controls the editor's panels share: a labelled slider, a row of
 * colour swatches, a grid of icon tiles, a panel section heading.
 */
import React from 'react';

import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';

export const PanelSection: React.FC<{ title: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; className?: string }> = ({
    title,
    action,
    children,
    className,
}) => (
    <section className={cn('flex flex-col gap-3 border-b px-4 py-4 last:border-b-0', className)}>
        <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-medium text-muted-foreground">{title}</h3>
            {action}
        </div>
        {children}
    </section>
);

export const SliderRow: React.FC<{
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    format?: (v: number) => string;
    onChange: (v: number) => void;
    disabled?: boolean;
}> = ({ label, value, min, max, step = 0.01, format, onChange, disabled }) => (
    <div className={cn('flex flex-col gap-2', disabled && 'opacity-50')}>
        <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{label}</span>
            <span className="tabular-nums text-foreground">{format ? format(value) : value.toFixed(2)}</span>
        </div>
        <Slider
            value={[value]}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            onValueChange={(v: number[]) => onChange(v[0])}
        />
    </div>
);

export const ColorRow: React.FC<{
    value: string;
    swatches: string[];
    onChange: (color: string) => void;
    label?: string;
}> = ({ value, swatches, onChange, label }) => {
    const unique = Array.from(new Set(swatches.map((s) => s.toUpperCase())));
    return (
        <div className="flex flex-col gap-2">
            {label && <span className="text-xs text-muted-foreground">{label}</span>}
            <div className="flex flex-wrap items-center gap-1.5">
                {unique.map((c) => (
                    <button
                        key={c}
                        type="button"
                        title={c}
                        aria-label={c}
                        onClick={() => onChange(c)}
                        className={cn(
                            'size-6 rounded-full border border-border transition-shadow',
                            value?.toUpperCase() === c && 'ring-[1.5px] ring-foreground ring-offset-2 ring-offset-background',
                        )}
                        style={{ background: c }}
                    />
                ))}
                <label className="relative flex size-6 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-dashed border-border text-[10px] text-muted-foreground hover:border-foreground">
                    +
                    <input
                        type="color"
                        value={/^#[0-9a-f]{6}$/i.test(value || '') ? value : '#ffffff'}
                        onChange={(e) => onChange(e.target.value.toUpperCase())}
                        className="absolute inset-0 cursor-pointer opacity-0"
                    />
                </label>
            </div>
        </div>
    );
};

export interface TileOption<T extends string> {
    value: T;
    label: string;
    icon?: React.ReactNode;
}

export function TileGrid<T extends string>({
    value,
    options,
    onChange,
    columns = 3,
}: {
    value: T | undefined;
    options: TileOption<T>[];
    onChange: (v: T) => void;
    columns?: number;
}) {
    return (
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => onChange(o.value)}
                        className={cn(
                            'flex flex-col items-center gap-1.5 rounded-lg border px-1 py-2 text-[11px] transition-colors hover:bg-muted',
                            active ? 'border-foreground text-foreground outline outline-[0.5px] outline-foreground' : 'border-border text-muted-foreground',
                        )}
                    >
                        {o.icon && <span className="flex size-5 items-center justify-center [&_svg]:size-4">{o.icon}</span>}
                        <span className="truncate">{o.label}</span>
                    </button>
                );
            })}
        </div>
    );
}

export const NumberField: React.FC<{
    label: string;
    value: number;
    onChange: (v: number) => void;
    step?: number;
    min?: number;
    suffix?: string;
}> = ({ label, value, onChange, step = 0.1, min = 0, suffix }) => (
    <label className="flex flex-col gap-1.5 text-xs">
        <span className="text-muted-foreground">{label}</span>
        <div className="flex h-8 items-center rounded-lg border bg-background px-2 focus-within:border-ring">
            <input
                type="number"
                value={Number.isFinite(value) ? Math.round(value * 100) / 100 : 0}
                step={step}
                min={min}
                onChange={(e) => {
                    const n = parseFloat(e.target.value);
                    if (Number.isFinite(n)) onChange(Math.max(min, n));
                }}
                className="w-full min-w-0 bg-transparent text-sm tabular-nums outline-none"
            />
            {suffix && <span className="ps-1 text-muted-foreground">{suffix}</span>}
        </div>
    </label>
);
